import { z } from 'zod';
/**
 * Hard ceiling on how many connections one GET /connections call returns. Chosen above the largest
 * current all-organization response so existing callers are never truncated; the server enforces it
 * and reports omissions via `truncated`.
 */
export const CONNECTION_LIST_LIMIT_MAX = 1000;
/** The kind of integration a connection represents. */
export var ConnectionType;
(function (ConnectionType) {
    ConnectionType["Sftp"] = "Sftp";
    ConnectionType["Api"] = "Api";
    ConnectionType["InterfaceEngine"] = "InterfaceEngine";
    ConnectionType["IntegrationEngine"] = "IntegrationEngine";
})(ConnectionType || (ConnectionType = {}));
/**
 * One integration connection in the merged GET /connections view, drawn from four sources: SFTP
 * feeds, API instances, Mirth (InterfaceEngine) channels, and Resource Gateway (IntegrationEngine)
 * connections. The fields are the common shape those sources are projected onto — nothing here is
 * specific to retrieving a single connection.
 *
 * `detailPath` from the internal row is deliberately omitted: it addresses per-type detail routes
 * that are not part of this contract, so it is not usable by a caller of this endpoint.
 */
export const ConnectionSchema = z.object({
    connectionType: z
        .enum(ConnectionType)
        .describe('The kind of connection: Sftp, Api, InterfaceEngine (Mirth), or IntegrationEngine.'),
    connectionId: z.string().describe('Identifier for this connection within its source system.'),
    name: z.string().describe('Human-readable connection name.'),
    source: z.string().describe('Where data enters the connection, e.g. the sending system.'),
    destination: z.string().describe('Where data leaves the connection, e.g. the receiving system.'),
    method: z
        .string()
        .describe('Transport or integration method, e.g. the SFTP host or channel method.'),
    dataType: z
        .string()
        .describe('The kind of data the connection carries, e.g. an HL7 message type.'),
    organizationId: z.string().describe('Identifier of the organization that owns this connection.'),
    lastUpdated: z.iso
        .datetime()
        .describe('ISO-8601 timestamp of when the connection configuration was last changed. For ' +
        'InterfaceEngine (Mirth) connections this is served from a cache and may be up to one ' +
        'hour stale.'),
    lastActivityAt: z.iso
        .datetime()
        .nullable()
        .describe("ISO-8601 timestamp of the connection's most recent data activity, or null when unknown."),
    fetchedAt: z.iso
        .datetime()
        .nullable()
        .describe('ISO-8601 timestamp of when cached InterfaceEngine details were read; null for connection ' +
        'types fetched live on each request.'),
});
/**
 * Filters for GET /connections. The text filters are case-insensitive substring matches; the org
 * filter narrows to one organization the caller is authorized for, and `connectionType` matches
 * exactly.
 *
 * Strict so an unknown query parameter is rejected rather than silently ignored, matching the
 * validation the endpoint performs today.
 */
export const ConnectionListQuerySchema = z
    .object({
    organizationId: z
        .uuid()
        .optional()
        .describe('Return only connections owned by this organization, which the caller must be authorized for.'),
    connectionType: z
        .enum(ConnectionType)
        .optional()
        .describe('Return only connections of this type.'),
    dataType: z
        .string()
        .trim()
        .optional()
        .describe('Return only connections whose data type contains this text (case-insensitive).'),
    source: z
        .string()
        .trim()
        .optional()
        .describe('Return only connections whose source contains this text (case-insensitive).'),
    destination: z
        .string()
        .trim()
        .optional()
        .describe('Return only connections whose destination contains this text (case-insensitive).'),
    method: z
        .string()
        .trim()
        .optional()
        .describe('Return only connections whose method contains this text (case-insensitive).'),
    q: z
        .string()
        .trim()
        .optional()
        .describe('Return only connections matching this text (case-insensitive) in name, source, ' +
        'destination, method, data type, or connection type.'),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(CONNECTION_LIST_LIMIT_MAX)
        .default(CONNECTION_LIST_LIMIT_MAX)
        .describe('Maximum number of connections to return. Matches beyond this are omitted and `truncated` ' +
        'is set; narrow the query with filters rather than paging.'),
})
    .strict();
//# sourceMappingURL=connection.js.map